/**
 * Diary Firestore access — web mirror of services/firebase/diary.ts.
 * Same collection (ksnap_diary_entries), same doc shapes; entries written here
 * render identically in the mobile app and vice versa.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  limit as qLimit,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS } from '@/lib/collections'
import type { DiaryEntry, DiaryEntryInput, MealTime } from '@/types'

/** Safely convert a Firestore Timestamp, Date, or ISO string to a Date. */
function toDate(value: unknown): Date | undefined {
  if (!value) return undefined
  const v = value as { toDate?: () => Date }
  if (typeof v.toDate === 'function') return v.toDate()
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') return new Date(value)
  return undefined
}

function fromDoc(id: string, data: Record<string, unknown>): DiaryEntry {
  return {
    ...data,
    id,
    timestamp: toDate(data.timestamp) ?? new Date(),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  } as DiaryEntry
}

const entriesCol = () => collection(db, COLLECTIONS.DIARY_ENTRIES)

export const DiaryService = {
  async createEntry(userId: string, entry: DiaryEntryInput): Promise<string> {
    // Deep-clean undefined values (Firestore rejects undefined at any depth) —
    // matches mobile createEntry. Date fields are re-applied after the round-trip.
    const { timestamp, ...rest } = entry
    const cleanEntry = JSON.parse(JSON.stringify(rest))
    const ref = await addDoc(entriesCol(), {
      ...cleanEntry,
      userId,
      timestamp: timestamp || serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return ref.id
  },

  subscribeToEntriesForDate(
    userId: string,
    date: string,
    onUpdate: (entries: DiaryEntry[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const q = query(
      entriesCol(),
      where('userId', '==', userId),
      where('date', '==', date),
      orderBy('timestamp', 'asc'),
    )
    return onSnapshot(
      q,
      (snapshot) => {
        onUpdate(snapshot.docs.map((d) => fromDoc(d.id, d.data())))
      },
      (error) => {
        console.error('Error listening to diary entries:', error)
        onError?.(error)
      },
    )
  },

  async getEntriesForDate(userId: string, date: string): Promise<DiaryEntry[]> {
    const snap = await getDocs(
      query(
        entriesCol(),
        where('userId', '==', userId),
        where('date', '==', date),
        orderBy('timestamp', 'asc'),
      ),
    )
    return snap.docs.map((d) => fromDoc(d.id, d.data()))
  },

  async getEntriesForDateRange(
    userId: string,
    startDate: string,
    endDate: string,
  ): Promise<DiaryEntry[]> {
    const snap = await getDocs(
      query(
        entriesCol(),
        where('userId', '==', userId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc'),
        orderBy('timestamp', 'asc'),
      ),
    )
    return snap.docs.map((d) => fromDoc(d.id, d.data()))
  },

  /** Recent unique foods by name, most recent first (Recent Foods suggestions). */
  async getRecentEntries(userId: string, limit = 10): Promise<DiaryEntry[]> {
    const snap = await getDocs(
      query(
        entriesCol(),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        qLimit(limit * 3),
      ),
    )
    const seen = new Set<string>()
    const entries: DiaryEntry[] = []
    for (const d of snap.docs) {
      if (entries.length >= limit) break
      const data = d.data()
      const name = (data.name as string | undefined)?.toLowerCase()
      if (name && !seen.has(name)) {
        seen.add(name)
        entries.push(fromDoc(d.id, data))
      }
    }
    return entries
  },

  async updateEntry(entryId: string, updates: Partial<DiaryEntry>): Promise<void> {
    const { id: _id, createdAt: _c, timestamp, ...updateData } = updates
    const cleanUpdates = JSON.parse(JSON.stringify(updateData))
    await updateDoc(doc(db, COLLECTIONS.DIARY_ENTRIES, entryId), {
      ...cleanUpdates,
      ...(timestamp !== undefined && { timestamp }),
      updatedAt: serverTimestamp(),
    })
  },

  async deleteEntry(entryId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.DIARY_ENTRIES, entryId))
  },

  async copyEntriesForDate(
    userId: string,
    sourceDate: string,
    targetDate: string,
    targetMealType?: MealTime,
  ): Promise<number> {
    const sourceEntries = await this.getEntriesForDate(userId, sourceDate)
    if (sourceEntries.length === 0) return 0
    const batch = writeBatch(db)
    const now = new Date()
    for (const entry of sourceEntries) {
      const { id: _id, createdAt: _c, updatedAt: _u, timestamp: _t, ...entryData } = entry
      batch.set(doc(entriesCol()), {
        ...JSON.parse(JSON.stringify(entryData)),
        date: targetDate,
        mealType: targetMealType || entry.mealType,
        timestamp: now,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
    return sourceEntries.length
  },

  async copySingleEntry(
    entry: DiaryEntry,
    targetDate: string,
    targetMealType?: MealTime,
  ): Promise<string> {
    const { id: _id, createdAt: _c, updatedAt: _u, timestamp: _t, ...entryData } = entry
    const ref = doc(entriesCol())
    const batch = writeBatch(db)
    batch.set(ref, {
      ...JSON.parse(JSON.stringify(entryData)),
      date: targetDate,
      mealType: targetMealType || entry.mealType,
      timestamp: new Date(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    await batch.commit()
    return ref.id
  },

  async copyMultipleEntries(
    entries: DiaryEntry[],
    targetDate: string,
    targetMealType?: MealTime,
  ): Promise<number> {
    const entriesToCopy = entries.filter((e) => e.date !== targetDate)
    if (entriesToCopy.length === 0) return 0
    const batch = writeBatch(db)
    const now = new Date()
    for (const entry of entriesToCopy) {
      const { id: _id, createdAt: _c, updatedAt: _u, timestamp: _t, ...entryData } = entry
      batch.set(doc(entriesCol()), {
        ...JSON.parse(JSON.stringify(entryData)),
        date: targetDate,
        mealType: targetMealType || entry.mealType,
        timestamp: now,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
    return entriesToCopy.length
  },
}
